import { positionChecker, usModelOfAtomsChecker } from 'src/logic'
import { I18NText, Position, State, StateObj, Language } from 'src/types'
import { checkAtomsWeb2 } from 'src/features/atoms/glove/logic'
import { SELECTABLE_PARTS_LIST_WITH_PRINT_LEATHER_TYPE, SELECTABLE_PARTS_LIST_WITH_PRINT_LEATHER_TYPE_4_CATCHER } from '../japan/constants/colors'
import { en2JaBasePartsTranslates } from '../us/constants/bases'
import { en2JaColorPartsTranslates } from '../us/constants/colors'
import { ko2JaColorPartsTranslates } from '../ko/constants/colors'
import { ko2JaBasePartsTranslates } from '../ko/constants/bases'
import {
  AGL_NE008_AKG_GT07_PRODUCT_NUMBER,
  AGL_NE001_AKG_1_PRODUCT_NUMBER,
  AGL_NE011_AKG_21_PRODUCT_NUMBER,
  AGL_NE102_AKG_5_PRODUCT_NUMBER,
  AGL_NE105_PRODUCT_NUMBER,
  AGL_NE501_PRODUCT_NUMBER,
  AGL_NE801_PRODUCT_NUMBER,
  AKG_11_PRODUCT_NUMBER,
  AKG_16_PRODUCT_NUMBER,
  AKG_4_PRODUCT_NUMBER,
  AKG_5_PRODUCT_NUMBER,
  AKG_6_PRODUCT_NUMBER,
  AKG_JS4_PLUS_PRODUCT_NUMBER,
  AKG_JS5_PRODUCT_NUMBER,
  APL_UR002_PLUS_PRODUCT_NUMBER,
  APL_UR003_PLUS_PRODUCT_NUMBER,
  APL_UR004_PLUS_PRODUCT_NUMBER,
  APL_UR005_PLUS_PRODUCT_NUMBER,
  APL_UR006_PLUS_PRODUCT_NUMBER,
  APL_UR007_PLUS_PRODUCT_NUMBER,
  APL_UR015_PLUS_PRODUCT_NUMBER,
  APL_UR026_PLUS_PRODUCT_NUMBER,
  APL_UR4_PRODUCT_NUMBER,
  APL_UR066_PLUS_PRODUCT_NUMBER,
  APL_UR008_PLUS_PRODUCT_NUMBER,
  APL_NE102_PRODUCT_NUMBER,
  APL_NE021_PRODUCT_NUMBER,
  APL_NE026_PRODUCT_NUMBER,
  APL_NE066_PRODUCT_NUMBER,
  APL_NE702_PRODUCT_NUMBER,
  AGL_105_PRODUCT_NUMBER,
  AKG_GT07_PRODUCT_NUMBER,
  AGL_501_PRODUCT_NUMBER,
  PRO46_AKG_1_PRODUCT_NUMBER,
  AKG_7_PRODUCT_NUMBER,
  AKG_27_PRODUCT_NUMBER,
  AGL_702_PRODUCT_NUMBER,
  AKG_13_PRODUCT_NUMBER,
  AGL_801_PRODUCT_NUMBER,
  AGL_1001_PRODUCT_NUMBER,
  AGL_1004_PRODUCT_NUMBER,
  AGL_1006_PRODUCT_NUMBER,
  AGL_NE003_AKG_GT33_PRODUCT_NUMBER,
  AGL_3001_PRODUCT_NUMBER,
  AGL_2001_PRODUCT_NUMBER
} from '../japan/constants/baseModel'
import { tw2JaBasePartsTranslates } from '../tw/constants/bases'
import { tw2JaColorPartsTranslates } from '../tw/constants/colors'
import { getI18NText } from 'src/locales/utils'
import { atomsFlagGenerator } from '../us/logic/baseSetting'

// TODO: ここで１レコードずつ翻訳しているのは効率が悪い
const genGloveBaseCell = (head: string, stateObj: StateObj, language: Language = 'ja') => {
  const isEnglish = language === 'en'
  const isZhTw = language === 'zh-TW'

  if (isEnglish || isZhTw) {
    // シミュレーション中は英語表示 or 繁体字表示のまま
    return { head, label: stateObj?.label, value: stateObj?.value }
  }
  // 小売店画面では日本語表示に変換する
  const translatedLabelFromEn = en2JaBasePartsTranslates.find((x) => x?.en === stateObj?.label)?.japanese
  const translatedLabelFromKo = ko2JaBasePartsTranslates.find((x) => x?.korea === stateObj?.label)?.japanese
  const translatedLabelFromTw = tw2JaBasePartsTranslates.find((x) => x?.taiwan === stateObj?.label)?.japanese
  const translatedLabel = translatedLabelFromEn || translatedLabelFromKo || translatedLabelFromTw || stateObj?.label
  return { head, label: translatedLabel, value: stateObj?.value }
}

// グローブ / 基本項目ロジック
export const getAtomsBaseCells = (state: State, position: Position, t: I18NText, language: Language = 'ja') => {
  const tt = getI18NText(language, t)
  const { isPitcher, isGlove, isCatcher } = positionChecker(position)
  const productNumber = state.baseModel.productNumber
  const baseModelStateObj = {
    label: productNumber,
    value: productNumber
  }
  const isUsModel = usModelOfAtomsChecker(productNumber)
  const isJapanModel = !isUsModel
  const isAGL_NE105 = state.baseModel.productNumber === AGL_NE105_PRODUCT_NUMBER
  const { isCatcherAndOtherThanAplSeries } = atomsFlagGenerator(state, position)
  return [
    isJapanModel && genGloveBaseCell(tt.KINDS, state?.kinds, language),
    genGloveBaseCell(tt.BASE_MODEL, baseModelStateObj, language),
    genGloveBaseCell(tt.ATOMS_LEATHER, state.tanner, language),
    genGloveBaseCell(tt.DOMINANT_ARM, state.dominantArm, language),
    isGlove && genGloveBaseCell(tt.WEB, state.webParts, language),
    (isPitcher || isCatcherAndOtherThanAplSeries) && !isAGL_NE105 && isJapanModel && genGloveBaseCell(tt.BACK_STYLE, state.backStyle, language),
    genGloveBaseCell(tt.FINGER_GUARD, state.fingerGuardType, language),
    isPitcher && !isAGL_NE105 && genGloveBaseCell(tt.MATERIAL_PACK, state.materialPack, language),
    isGlove && genGloveBaseCell(tt.FINGER_SIZE, state.size, language),
    isGlove && genGloveBaseCell(tt.BACK_LACE, state.backLace, language), // 投手以外もOK
    isJapanModel && !isCatcher && genGloveBaseCell(tt.LOOP_OF_RING, state.loopOfRingFinger, language),
    isJapanModel && genGloveBaseCell(tt.PRINT_LEATHER_TYPE, state.printLeatherType, language),
    isUsModel && genGloveBaseCell(tt.MOLDING_ORDER, state?.moldingOrder, language)
  ].filter(Boolean)
}

// グローブ / カラー項目ロジック
const getAtomsColorCell = (head: string, stateObj: StateObj, partsKey: string, language: Language = 'ja') => {
  const isEnglish = language === 'en'
  const isZhTw = language === 'zh-TW'
  if (isEnglish || isZhTw) {
    return { head, label: stateObj?.label, color: stateObj?.color, value: stateObj?.value, partsKey }
  }
  const translatedLabelFromEn = en2JaColorPartsTranslates.find((x) => x?.en === stateObj?.label)?.japanese
  const translatedLabelFromKo = ko2JaColorPartsTranslates.find((x) => x?.korea === stateObj?.label)?.japanese
  const translatedLabelFromTw = tw2JaColorPartsTranslates.find((x) => x?.taiwan === stateObj?.label)?.japanese
  const translatedLabel = translatedLabelFromEn || translatedLabelFromKo || translatedLabelFromTw || stateObj?.label
  // state.object.color からoptionalを削ると現状buildに失敗するため、このままにしておく。
  // initialStateで何かが不足していると予想
  return { head, label: translatedLabel, color: stateObj?.color, value: stateObj?.value, partsKey }
}

const variableColorPartsGenerator = (state: State, position: Position, t: I18NText, language: Language = 'ja') => {
  const productNumber = state.baseModel.productNumber
  const isUsModel = usModelOfAtomsChecker(productNumber)
  const { is2025Apl, isDefaultWristBelt } = seriesChecker(productNumber)
  const isUBack = state.baseModel.isUBack
  const backStyle = state.backStyle.value
  const isBelt = !isUsModel && (is2025Apl || isDefaultWristBelt)

  switch (position) {
    case 'pitcher':
      switch (backStyle) {
        case 'firstBackStyle':
          return [
            getAtomsColorCell(t.THUMB1, state.thumbOut, 'thumbOut', language),
            getAtomsColorCell(t.THUMB2_INDEX_MIDDLE5, state.thumbIndexMiddle, 'thumbIndexMiddle', language),
            getAtomsColorCell(t.MIDDLE6_RING7, state.middleLeftRingRight, 'middleLeftRingRight', language),
            getAtomsColorCell(t.RING8_LITTLE9, state.ringLeftLittleRight, 'ringLeftLittleRight', language),
            getAtomsColorCell(t.LITTLE10, state.littleOut, 'littleOut', language),
            getAtomsColorCell(t.FIRST_BACK_BELT, state.listBelt, 'listBelt', language) // ベルト必須
          ].filter(Boolean)
        case 'crownBackStyle':
          // ベルト一体型を考慮＿済
          return [
            getAtomsColorCell(t.THUMB1, state.thumbOut, 'thumbOut', language),
            getAtomsColorCell(t.THUMB2, state.thumbWeb, 'thumbWeb', language),
            getAtomsColorCell(t.INDEX3, state.indexWeb, 'indexWeb', language),
            getAtomsColorCell(t.INDEX4_MIDDLE5, state.indexLeftMiddleRight, 'indexLeftMiddleRight', language),
            getAtomsColorCell(t.MIDDLE6_RING7, state.middleLeftRingRight, 'middleLeftRingRight', language),
            getAtomsColorCell(t.RING8_LITTLE9, state.ringLeftLittleRight, 'ringLeftLittleRight', language),
            getAtomsColorCell(t.LITTLE10, state.littleOut, 'littleOut', language),
            isBelt && getAtomsColorCell(t.BAND, state.listBelt, 'listBelt', language)
          ].filter(Boolean)
        default:
          // case 'beltBackStyle': // グラブ
          // ベルト一体型を考慮＿済
          return [
            getAtomsColorCell(t.THUMB1, state.thumbOut, 'thumbOut', language),
            getAtomsColorCell(t.THUMB2, state.thumbWeb, 'thumbWeb', language),
            isUsModel && getAtomsColorCell(t.THUMB_ROOT, state.thumbRoot, 'thumbRoot', language),
            getAtomsColorCell(t.INDEX3, state.indexWeb, 'indexWeb', language),
            getAtomsColorCell(t.INDEX4, state.indexMiddle, 'indexMiddle', language),
            getAtomsColorCell(t.MIDDLE5, state.middleIndex, 'middleIndex', language),
            getAtomsColorCell(t.MIDDLE6, state.middleRing, 'middleRing', language),
            getAtomsColorCell(t.RING7, state.ringMiddle, 'ringMiddle', language),
            getAtomsColorCell(t.RING8, state.ringLittle, 'ringLittle', language),
            getAtomsColorCell(t.LITTLE9, state.littleRing, 'littleRing', language),
            getAtomsColorCell(t.LITTLE10, state.littleOut, 'littleOut', language),
            isBelt && getAtomsColorCell(t.BAND, state.listBelt, 'listBelt', language)
          ].filter(Boolean)
      }
    case 'infielder':
    case 'outfielder':
      if (isUBack) {
        return [
          getAtomsColorCell(t.THUMB1, state.thumbOut, 'thumbOut', language),
          getAtomsColorCell(t.THUMB2_INDEX3, state.thumbWebAndIndexWeb, 'thumbWebAndIndexWeb', language),
          getAtomsColorCell(t.INDEX4, state.indexMiddle, 'indexMiddle', language),
          getAtomsColorCell(t.MIDDLE5, state.middleIndex, 'middleIndex', language),
          getAtomsColorCell(t.MIDDLE6, state.middleRing, 'middleRing', language),
          getAtomsColorCell(t.RING7, state.ringMiddle, 'ringMiddle', language),
          getAtomsColorCell(t.RING8, state.ringLittle, 'ringLittle', language),
          getAtomsColorCell(t.LITTLE9, state.littleRing, 'littleRing', language),
          getAtomsColorCell(t.LITTLE10, state.littleOut, 'littleOut', language),
          isBelt && getAtomsColorCell(t.BAND, state.listBelt, 'listBelt', language)
        ].filter(Boolean)
      }
      return [
        getAtomsColorCell(t.THUMB1, state.thumbOut, 'thumbOut', language),
        getAtomsColorCell(t.THUMB2, state.thumbWeb, 'thumbWeb', language),
        isUsModel && getAtomsColorCell(t.THUMB_ROOT, state.thumbRoot, 'thumbRoot', language),
        getAtomsColorCell(t.INDEX3, state.indexWeb, 'indexWeb', language),
        getAtomsColorCell(t.INDEX4, state.indexMiddle, 'indexMiddle', language),
        getAtomsColorCell(t.MIDDLE5, state.middleIndex, 'middleIndex', language),
        getAtomsColorCell(t.MIDDLE6, state.middleRing, 'middleRing', language),
        getAtomsColorCell(t.RING7, state.ringMiddle, 'ringMiddle', language),
        getAtomsColorCell(t.RING8, state.ringLittle, 'ringLittle', language),
        getAtomsColorCell(t.LITTLE9, state.littleRing, 'littleRing', language),
        getAtomsColorCell(t.LITTLE10, state.littleOut, 'littleOut', language),
        isBelt && getAtomsColorCell(t.BAND, state.listBelt, 'listBelt', language)
      ].filter(Boolean)
    default:
      return []
  }
}

export const getAtomsGloveColorCells = (state: State, position: Position, t: I18NText, language: Language = 'ja') => {
  const tt = getI18NText(language, t)
  const needFingerCoverOrPad = state.fingerGuardType?.value !== 'none'
  const needWeb2 = checkAtomsWeb2(state.webParts?.value, position)
  const isPrintLeatherTypeSelected =
    state.printLeatherType?.value && state.printLeatherType?.value !== 'unselected' && state.printLeatherType?.value !== 'none'
  const variablePartsCells = variableColorPartsGenerator(state, position, tt, language)
  const isFirstBackStyle = state.backStyle.value === 'firstBackStyle'
  const printLeatherTypeParts = isFirstBackStyle
    ? [...SELECTABLE_PARTS_LIST_WITH_PRINT_LEATHER_TYPE, 'listBelt']
    : SELECTABLE_PARTS_LIST_WITH_PRINT_LEATHER_TYPE
  return [
    getAtomsColorCell(tt.CATCH_SURFACE, state.catchFace, 'catchFace', language),
    getAtomsColorCell(tt.WEB, state.web, 'web', language),
    needWeb2 && getAtomsColorCell(tt.WEB2, state.web2, 'web2', language),
    getAtomsColorCell(tt.THUMB_HOOK, state.thumbHook, 'thumbHook', language),
    getAtomsColorCell(tt.LITTLE_HOOK, state.littleHook, 'littleHook', language),
    needFingerCoverOrPad && getAtomsColorCell(tt.FINGER_GUARD, state.indexCover, 'indexCover', language),
    getAtomsColorCell(tt.LININGS, state.linings, 'linings', language),
    ...variablePartsCells,
    getAtomsColorCell(tt.EDGE, state.edge, 'edge', language),
    getAtomsColorCell(tt.HAMIDASHI, state.hamidashi, 'hamidashi', language),
    getAtomsColorCell(tt.LACE, state.lace, 'lace', language),
    getAtomsColorCell(tt.STITCH, state.stitch, 'stitch', language),
    !isFirstBackStyle ? getAtomsColorCell(tt.MOUTON, state.mouton, 'mouton', language) : null,
    getAtomsColorCell(tt.LABEL, state.atomsLabel, 'atomsLabel', language)
  ]
    .filter(Boolean)
    .filter((item) => (isPrintLeatherTypeSelected ? printLeatherTypeParts.includes(item.partsKey) : true))
}

// ファーストミット / カラー項目ロジック
export const getAtomsFirstMittColorCells = (state: State, t: I18NText, language: Language = 'ja') => {
  const tt = getI18NText(language, t)
  const isAplSeries = aplSeriesChecker(state.baseModel.productNumber)
  const needFingerCoverOrPad = state.fingerGuardType.value !== 'none'
  const isPrintLeatherTypeSelected =
    state.printLeatherType?.value && state.printLeatherType?.value !== 'unselected' && state.printLeatherType?.value !== 'none'

  return [
    getAtomsColorCell(tt.CATCH_SURFACE, state.catchFace, 'catchFace', language),
    getAtomsColorCell(tt.WEB, state.web, 'web', language),
    getAtomsColorCell(tt.WEB2, state.web2, 'web2', language),
    getAtomsColorCell(tt.THUMB_HOOK, state.thumbHook, 'thumbHook', language),
    getAtomsColorCell(tt.LITTLE_HOOK, state.littleHook, 'littleHook', language),
    needFingerCoverOrPad && getAtomsColorCell(tt.FINGER_GUARD, state.indexCover, 'indexCover', language),
    getAtomsColorCell(tt.LININGS, state.linings, 'linings', language),
    getAtomsColorCell(tt.THUMB, state.thumb, 'thumb', language),
    getAtomsColorCell(tt.UNDER_WEB, state.underWeb, 'underWeb', language),
    getAtomsColorCell(tt.BOOMERANG, state.boomerang, 'boomerang', language),
    !isAplSeries && getAtomsColorCell(tt.BAND, state.listBelt, 'listBelt', language),
    getAtomsColorCell(tt.LACE, state.lace, 'lace', language),
    getAtomsColorCell(tt.EDGE, state.edge, 'edge', language),
    getAtomsColorCell(tt.STITCH, state.stitch, 'stitch', language),
    getAtomsColorCell(tt.MOUTON, state.mouton, 'mouton', language),
    getAtomsColorCell(tt.LABEL, state.atomsLabel, 'atomsLabel', language)
  ]
    .filter(Boolean)
    .filter((item) => (isPrintLeatherTypeSelected ? SELECTABLE_PARTS_LIST_WITH_PRINT_LEATHER_TYPE.includes(item.partsKey) : true))
}

// キャッチャーミット / カラー項目ロジック
export const getAtomsCatcherMittColorCells = (state: State, t: I18NText, language: Language = 'ja') => {
  const tt = getI18NText(language, t)
  const needFingerCoverOrPad = state.fingerGuardType.value !== 'none'
  const backStyleValue = state.baseModel.productNumber === APL_UR002_PLUS_PRODUCT_NUMBER ? state.baseModel.backStyle : state.backStyle.value
  const isRegularBackStyle = backStyleValue === 'regularBackStyle'
  const isBeltBackStyle = backStyleValue === 'beltBackStyle'
  const isPrintLeatherTypeSelected =
    state.printLeatherType?.value && state.printLeatherType?.value !== 'unselected' && state.printLeatherType?.value !== 'none'

  return [
    getAtomsColorCell(tt.CATCH_SURFACE, state.catchFace, 'catchFace', language),
    getAtomsColorCell(tt.LACE, state.lace, 'lace', language),
    getAtomsColorCell(tt.WEB, state.web, 'web', language),
    getAtomsColorCell(tt.EDGE, state.edge, 'edge', language),
    getAtomsColorCell(tt.STITCH, state.stitch, 'stitch', language),
    getAtomsColorCell(tt.THUMB_HOOK, state.thumbHook, 'thumbHook', language),
    getAtomsColorCell(tt.LITTLE_HOOK, state.littleHook, 'littleHook', language),
    needFingerCoverOrPad && getAtomsColorCell(tt.FINGER_GUARD, state.indexCover, 'indexCover', language),
    getAtomsColorCell(tt.LININGS, state.linings, 'linings', language),
    getAtomsColorCell(tt.STAND, state.fingerStand, 'fingerStand', language),
    getAtomsColorCell(tt.THUMB_INDEX_MIDDLE5, state.thumbIndexMiddleRight, 'thumbIndexMiddleRight', language), // 親指・人差指・中指5
    isRegularBackStyle && getAtomsColorCell(tt.MIDDLE6_RING, state.middleLeftRing, 'middleLeftRing', language),
    isRegularBackStyle && getAtomsColorCell(tt.LITTLE10, state.littleOut, 'littleOut', language),
    isRegularBackStyle && getAtomsColorCell(tt.FINGER_TIP_OUT, state.indexFingerTipOut, 'indexFingerTipOut', language),
    isRegularBackStyle && getAtomsColorCell(tt.BELT, state.listBelt, 'listBelt', language),
    isBeltBackStyle && getAtomsColorCell(tt.MIDDLE6_RING_PINKY, state.middleLeftRingLittle, 'middleLeftRingLittle', language),
    getAtomsColorCell(tt.HAMIDASHI, state.hamidashi, 'hamidashi', language),
    getAtomsColorCell(tt.TARGET, state.catchFaceTarget, 'catchFaceTarget', language),
    isBeltBackStyle && getAtomsColorCell(tt.MOUTON, state.mouton, 'mouton', language),
    getAtomsColorCell(tt.LABEL, state.atomsLabel, 'atomsLabel', language)
  ]
    .filter(Boolean)
    .filter((item) => (isPrintLeatherTypeSelected ? SELECTABLE_PARTS_LIST_WITH_PRINT_LEATHER_TYPE_4_CATCHER.includes(item.partsKey) : true))
}

export const aplSeriesChecker = (productNumber: string): boolean =>
  [
    AGL_NE001_AKG_1_PRODUCT_NUMBER, // 投手
    AGL_NE011_AKG_21_PRODUCT_NUMBER, // 投手
    AGL_NE105_PRODUCT_NUMBER, // オールラウンド
    AGL_NE501_PRODUCT_NUMBER,
    AGL_NE102_AKG_5_PRODUCT_NUMBER,
    AGL_NE801_PRODUCT_NUMBER, // 外野手
    AGL_NE008_AKG_GT07_PRODUCT_NUMBER, // 外野手
    APL_NE102_PRODUCT_NUMBER,
    APL_NE021_PRODUCT_NUMBER,
    APL_NE026_PRODUCT_NUMBER,
    APL_NE066_PRODUCT_NUMBER,
    APL_NE702_PRODUCT_NUMBER, // 外野手
    APL_UR026_PLUS_PRODUCT_NUMBER, // ~2024
    APL_UR004_PLUS_PRODUCT_NUMBER, // ~2024
    APL_UR005_PLUS_PRODUCT_NUMBER, // ~2024
    APL_UR006_PLUS_PRODUCT_NUMBER, // ~2024
    APL_UR066_PLUS_PRODUCT_NUMBER, // ~2024
    APL_UR007_PLUS_PRODUCT_NUMBER, // ~2024
    APL_UR008_PLUS_PRODUCT_NUMBER, // ~2024
    APL_UR003_PLUS_PRODUCT_NUMBER, // ~2024
    APL_UR002_PLUS_PRODUCT_NUMBER, // ~2024
    APL_UR015_PLUS_PRODUCT_NUMBER // ~2024
  ].includes(productNumber)

export const seriesChecker = (
  productNumber: string
): {
  is2025Agl: boolean
  is2025Apl: boolean
  is2024Apl: boolean
  isDefaultWristBelt: boolean
} => {
  const isDefaultWristBelt = [
    AGL_NE001_AKG_1_PRODUCT_NUMBER, // 投手 2025
    AGL_NE011_AKG_21_PRODUCT_NUMBER, // 投手 2025
    PRO46_AKG_1_PRODUCT_NUMBER, // 投手 2024
    AKG_11_PRODUCT_NUMBER, // 投手 2024
    AKG_JS4_PLUS_PRODUCT_NUMBER, // 内野 2024
    AKG_JS5_PRODUCT_NUMBER, // 内野 2024
    AKG_4_PRODUCT_NUMBER, // 内野 2024
    AKG_5_PRODUCT_NUMBER, // 内野 2024
    AKG_6_PRODUCT_NUMBER, // 内野 2024
    AKG_16_PRODUCT_NUMBER, // 内野 2024
    APL_UR4_PRODUCT_NUMBER, // 内野 2024
    AGL_105_PRODUCT_NUMBER, // 内野 2024
    AGL_501_PRODUCT_NUMBER, // 内野 2024
    AKG_GT07_PRODUCT_NUMBER, // 外野 2024
    AKG_7_PRODUCT_NUMBER, // 外野 2024
    AKG_27_PRODUCT_NUMBER, // 外野 2024
    AKG_13_PRODUCT_NUMBER, // 一塁手用 2024

    AGL_702_PRODUCT_NUMBER, // 外野 2024
    AGL_801_PRODUCT_NUMBER, // 外野 2024
    AGL_1001_PRODUCT_NUMBER, // 少年軟式 2024
    AGL_1004_PRODUCT_NUMBER, // 少年軟式 2024
    AGL_1006_PRODUCT_NUMBER, // 少年軟式 2024
    AGL_NE003_AKG_GT33_PRODUCT_NUMBER, // 一塁手用 2025
    AGL_3001_PRODUCT_NUMBER, // 少年一塁手
    AGL_2001_PRODUCT_NUMBER // 少年捕手
  ].includes(productNumber)

  const is2025Agl = [
    AGL_NE001_AKG_1_PRODUCT_NUMBER, // 投手
    AGL_NE011_AKG_21_PRODUCT_NUMBER, // 投手
    AGL_NE105_PRODUCT_NUMBER, // オールラウンド
    AGL_NE501_PRODUCT_NUMBER,
    AGL_NE102_AKG_5_PRODUCT_NUMBER,
    AGL_NE801_PRODUCT_NUMBER, // 外野手
    AGL_NE008_AKG_GT07_PRODUCT_NUMBER // 外野手
  ].includes(productNumber)

  const check2025Apl = (str) => /^APL-NE(102|021|026|066|702)$/.test(str)
  const is2025Apl =
    check2025Apl(productNumber) ||
    [APL_NE102_PRODUCT_NUMBER, APL_NE021_PRODUCT_NUMBER, APL_NE026_PRODUCT_NUMBER, APL_NE066_PRODUCT_NUMBER, APL_NE702_PRODUCT_NUMBER].includes(
      productNumber
    )
  const check2024Apl = (str) => /^APL-UR(066|006|026|004|005|007)\+$/.test(str)
  const is2024Apl = check2024Apl(productNumber)
  return {
    is2025Agl,
    is2025Apl,
    is2024Apl,
    isDefaultWristBelt
  }
}
// 品番を定数で持つべし
